import keycloakApi from './keycloak-auth';
import type { PaymentWithRelations } from './payments';

// Types based on backend DTOs and entities
export interface SupplierData {
  id: string;
  first_name: string;
  last_name: string;
  business_name: string | null;
  type: string;
  status: string;
  client_party_id: string;
  client_name: string | null;
  preferred_payment_method: string | null;
  phone_number: string | null;
  email: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip_code: string | null;
  verification_status: string | null;
  external_ref: string | null;
  relationship_approval: string | null;
  relationship_approval_at: Date | null;
  relationship_id: string | null;
  created_at: Date;
  updated_at: Date;
  created_by_name?: string | null;
  notes_count?: number;
}

// Detailed supplier types
export interface SupplierAddress {
  id: string;
  type: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string | null;
  postal_code: string | null;
  country: string;
  is_primary?: boolean;
}

export interface SupplierEmail {
  id: string;
  type: string;
  email_address: string;
  is_primary: boolean;
  is_verified: boolean;
}

export interface SupplierPhone {
  id: string;
  type: string;
  phone_number: string;
  formatted_number: string | null;
  is_primary: boolean;
  is_verified: boolean;
}

export interface SupplierRelationship {
  id: string;
  relationship_type: string;
  related_party_id: string;
  related_party_name: string;
  related_party_type: string;
  direction: 'from' | 'to';
  status: string;
  /** Count of notes on this relationship (from supplier detail API). */
  notes_count?: number;
}

export interface SupplierBankAccount {
  id: string;
  account_name: string | null;
  account_type: string;
  is_primary: boolean;
  is_verified: boolean;
  routing_number: string | null;
  account_number_masked: string | null;
  bank_name: string | null;
  status: string;
  /** When the account/method was added (ISO). */
  created_at?: string | null;
  /** Review decision after a bank return. */
  review_status?: 'NEEDS_REVIEW' | 'CLEARED' | null;
  reviewed_at?: string | null;
  /** Derived from the returned payment; null when never returned. */
  return_code?: string | null;
  return_reason?: string | null;
  returned_at?: string | null;
}

/** A unified payment method on file (ACH account, mailed check, or wallet). */
export interface PaymentMethodOnFile {
  id: string;
  /** ACH | CHECK | DIGITAL_WALLET | UNKNOWN */
  type: string;
  label: string;
  masked_detail: string | null;
  added_at: string | null;
  status: string;
  is_preferred: boolean;
  is_verified: boolean;
  review_status?: 'NEEDS_REVIEW' | 'CLEARED' | null;
  return_code?: string | null;
  return_reason?: string | null;
  returned_at?: string | null;
  eligible_for_reissue: boolean;
}

export interface SupplierVerificationMethod {
  code: string;
  name: string;
  status: string;
  verified_at?: string | null;
  /** Provider response data for tooltip errors (Lyons ACH, USPS, OFAC) */
  result_details?: Record<string, unknown> | null;
}

export interface SupplierDetailData extends SupplierData {
  date_of_birth?: string | null;
  national_id_masked?: string | null;
  registration_number?: string | null;
  tax_id_masked?: string | null;
  case_id?: string | null;
  addresses: SupplierAddress[];
  emails: SupplierEmail[];
  phones: SupplierPhone[];
  relationships: SupplierRelationship[];
  bank_accounts: SupplierBankAccount[];
  /** Unified list of all payment methods on file (ACH/CHECK/wallet). */
  payment_methods?: PaymentMethodOnFile[];
  /** True when any method is a returned method still awaiting replacement. */
  payment_method_needs_review?: boolean;
  payments?: PaymentWithRelations[];
  verification_methods?: SupplierVerificationMethod[];
}

export interface SuppliersPaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface SuppliersResponse {
  data: SupplierData[];
  pagination: SuppliersPaginationData;
}

export interface SupplierFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  status?: string | string[];
  type?: string | string[];
  /** Filters suppliers by client party id (internal users). */
  clientId?: string;
  /** Filters suppliers by relationship approval status (APPROVED, DENIED, NULL, IN_PROGRESS, etc). */
  relationshipApproval?: string | string[];
  /** Filters suppliers by preferred payment method code (ACH, CHECK, DIGITAL_WALLET). */
  preferredPaymentMethod?: string | string[];
  search?: string;
  /** 'all' = anyone, 'me' = only suppliers created by the current user */
  createdByFilter?: 'all' | 'me';
}

// API helper function with Keycloak bearer token
const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(endpoint, options);
};

// Suppliers API functions
export const getSuppliers = async (filters: SupplierFilters = {}): Promise<SuppliersResponse> => {
  const searchParams = new URLSearchParams();

  // Add filters to search params
  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.limit) searchParams.append('limit', filters.limit.toString());
  if (filters.sortBy) searchParams.append('sortBy', filters.sortBy);
  if (filters.order) searchParams.append('sortOrder', filters.order.toUpperCase());

  // Handle status filter - convert array to comma-separated string
  if (filters.status) {
    const statusValue = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
    searchParams.append('status', statusValue);
  }

  // Handle type filter - convert array to comma-separated string
  if (filters.type) {
    const typeValue = Array.isArray(filters.type) ? filters.type.join(',') : filters.type;
    searchParams.append('type', typeValue);
  }

  if (filters.clientId) searchParams.append('clientId', filters.clientId);

  if (filters.relationshipApproval != null && filters.relationshipApproval !== '') {
    const approvalList = (
      Array.isArray(filters.relationshipApproval)
        ? filters.relationshipApproval
        : [filters.relationshipApproval]
    )
      .map((a) => String(a).trim())
      .filter((a) => a.length > 0);
    if (approvalList.length > 0) {
      searchParams.append('relationshipApproval', approvalList.join(','));
    }
  }

  if (filters.preferredPaymentMethod != null && filters.preferredPaymentMethod !== '') {
    const methodList = (
      Array.isArray(filters.preferredPaymentMethod)
        ? filters.preferredPaymentMethod
        : [filters.preferredPaymentMethod]
    )
      .map((m) => String(m).trim())
      .filter((m) => m.length > 0);
    if (methodList.length > 0) {
      searchParams.append('preferredPaymentMethod', methodList.join(','));
    }
  }

  if (filters.search) searchParams.append('search', filters.search);
  if (filters.createdByFilter && filters.createdByFilter !== 'all') {
    searchParams.append('createdByFilter', filters.createdByFilter);
  }

  const queryString = searchParams.toString();
  const endpoint = `/suppliers${queryString ? `?${queryString}` : ''}`;

  return apiCall<SuppliersResponse>(endpoint);
};

// Create Individual DTO
export interface CreateIndividualData {
  party_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  additional_emails?: string[] | null;
  case_id: string;
  street: string;
  address2?: string | null;
  city: string;
  state: string;
  zip_code: string;
  payment_method: 'ACH' | 'CHECK' | 'DIGITAL_WALLET';
  bank_routing_number?: string | null;
  bank_account_number?: string | null;
  bank_account_type?: 'CHECKING' | 'SAVINGS' | null;
  is_beneficiary?: boolean;
  applicant_email?: string | null;
  beneficiary_email?: string | null;
  beneficiary_case_id?: string | null;
  beneficiary_street?: string | null;
  beneficiary_address2?: string | null;
  beneficiary_city?: string | null;
  beneficiary_state?: string | null;
  beneficiary_zip_code?: string | null;
  address_verification_status?: 'verified' | 'not_verified' | 'not_checked';
}

export type SupplierAddType = 'individual' | 'business';

export interface SupplierAddConfig {
  allowedTypes: SupplierAddType[];
  /** Normalized: 'supplier' | 'beneficiary' | custom (e.g. 'Vendor'). Use for UI labels. */
  displayName: string;
  /** When true show upload (batch) button; when true show add (individual) button. */
  supportedUploadTypes: { batch: boolean; individual: boolean };
  /** When set, use this label instead of "External Ref" in the UI */
  externalRefDisplayName?: string | null;
}

/** Singular and plural labels for the supplier/beneficiary term (e.g. Supplier/Suppliers, Beneficiary/Beneficiaries). */
export function getSupplierLabels(displayName: string): { singular: string; plural: string } {
  const normalized = (displayName || 'supplier').trim().toLowerCase();
  if (normalized === 'beneficiary') {
    return { singular: 'Beneficiary', plural: 'Beneficiaries' };
  }
  if (normalized === 'supplier' || normalized === '') {
    return { singular: 'Supplier', plural: 'Suppliers' };
  }
  const s = displayName.trim();
  const singular = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const plural = singular + (singular.endsWith('s') ? 'es' : 's');
  return { singular, plural };
}

// Get detailed supplier by ID
export const getSupplierById = async (id: string): Promise<SupplierDetailData> => {
  return apiCall<SupplierDetailData>(`/suppliers/${id}`);
};

/**
 * Per-party cache of in-flight/resolved add-config requests. The config is a
 * (near) static per-party value, but several pages/components request it for the
 * same party (app-level supplier-display context, payments, suppliers, detail
 * pages, add-supplier modal). Caching the promise dedupes concurrent calls into
 * a single network request and reuses the result across tab navigation, instead
 * of re-fetching every time a component mounts.
 */
const addConfigCache = new Map<string, Promise<SupplierAddConfig>>();

/**
 * Drop the cached add-config so the next call re-fetches. Pass a partyId to
 * invalidate a single party, or omit it to clear everything (e.g. on logout or
 * after settings that change display name / allowed types / external ref label).
 */
export const invalidateSupplierAddConfig = (partyId?: string): void => {
  if (partyId) {
    addConfigCache.delete(partyId);
  } else {
    addConfigCache.clear();
  }
};

/**
 * Get which supplier add flows (individual / business) are allowed for the given
 * client party. Results are cached per party (see {@link addConfigCache}); pass
 * `{ force: true }` to bypass the cache and re-fetch.
 */
export const getSupplierAddConfig = async (
  partyId: string,
  options?: { force?: boolean }
): Promise<SupplierAddConfig> => {
  if (!options?.force) {
    const cached = addConfigCache.get(partyId);
    if (cached) return cached;
  }

  const params = new URLSearchParams({ party_id: partyId });
  // apiCall (keycloak-auth) already unwraps { success, data } and returns data
  const request = apiCall<SupplierAddConfig>(`/suppliers/add-config?${params.toString()}`);
  addConfigCache.set(partyId, request);
  // Evict failed requests so a later call can retry instead of returning the rejection.
  request.catch(() => {
    if (addConfigCache.get(partyId) === request) {
      addConfigCache.delete(partyId);
    }
  });
  return request;
};

// Create individual supplier
export const createIndividual = async (data: CreateIndividualData): Promise<SupplierData> => {
  return apiCall<SupplierData>('/suppliers/individual', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/** Payload for PATCH /suppliers/:id (all fields optional) */
export interface UpdateSupplierPayload {
  first_name?: string | null;
  last_name?: string | null;
  business_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zip_code?: string | null;
  preferred_payment_method?: string | null;
  bank_routing_number?: string | null;
  bank_account_number?: string | null;
  bank_account_type?: string | null;
  bank_name?: string | null;
  address_verification_status?: 'verified' | 'not_verified' | 'not_checked';
  external_ref?: string | null;
}

export const updateSupplier = async (
  id: string,
  data: UpdateSupplierPayload
): Promise<SupplierDetailData> => {
  return apiCall<SupplierDetailData>(`/suppliers/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const setSupplierApproval = async (
  supplierId: string,
  approvalType: 'APPROVED' | 'DENIED'
): Promise<void> => {
  await apiCall<{ message: string }>(`/suppliers/${supplierId}/approval`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ approval_type: approvalType }),
  });
};

/** Payload for POST /suppliers/:id/payment-methods (add a replacement method). */
export interface AddPaymentMethodPayload {
  payment_method: 'ACH' | 'CHECK' | 'DIGITAL_WALLET';
  bank_routing_number?: string | null;
  bank_account_number?: string | null;
  bank_account_type?: 'CHECKING' | 'SAVINGS' | null;
  bank_name?: string | null;
  /** The returned method being replaced (marked CLEARED on success). */
  replaced_account_id?: string;
  address_verification_status?: 'verified' | 'not_verified' | 'not_checked';
}

/**
 * Add a replacement payment method after a bank return. Adds a NEW method
 * (keeping the returned one on file, flagged) and re-runs Lyons/OFAC/USPS.
 * Returns the refreshed supplier detail. Approve separately via
 * {@link setSupplierApproval}.
 */
export const addSupplierPaymentMethod = async (
  supplierId: string,
  data: AddPaymentMethodPayload
): Promise<SupplierDetailData> => {
  return apiCall<SupplierDetailData>(`/suppliers/${supplierId}/payment-methods`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

/**
 * Reveal the unmasked bank account number for one of a supplier's accounts.
 *
 * The default supplier detail endpoint only returns the masked value; this
 * call is what the edit modal uses to pre-populate the account-number field
 * when the user explicitly clicks Edit. Each call is recorded server-side
 * (`supplier.account_number.revealed`) so reveal events are auditable
 * independently of routine reads.
 */
export const revealSupplierAccountNumber = async (
  supplierId: string,
  accountId: string
): Promise<{ account_number: string }> => {
  return apiCall<{ account_number: string }>(
    `/suppliers/${supplierId}/accounts/${accountId}/account-number`
  );
};
